import { tenantInputSchema, type Tenant, type TenantInput } from '@provalia/shared';
import { useAsync } from '../../shared/ui';
import { api } from '../api';
import type { HubTunnel } from '../types';
import { CheckField, FormField } from './FormField';

/** Borrador del formulario de conexión: texto tal y como se teclea. */
export interface TenantDraft {
  slug: string;
  name: string;
  hubClient: string;
  slBaseUrl: string;
  slVerifySsl: boolean;
  slTimeoutMs: string;
  active: boolean;
}

export type TenantErrors = Partial<Record<keyof TenantDraft, string>>;

export const EMPTY_TENANT: TenantDraft = {
  slug: '',
  name: '',
  hubClient: '',
  slBaseUrl: '',
  slVerifySsl: false,
  slTimeoutMs: '30000',
  active: true,
};

export function draftFromTenant(t: Tenant): TenantDraft {
  return {
    slug: t.slug,
    name: t.name,
    hubClient: t.hubClient,
    slBaseUrl: t.slBaseUrl,
    slVerifySsl: t.slVerifySsl,
    slTimeoutMs: String(t.slTimeoutMs),
    active: t.active,
  };
}

/**
 * Valida con el mismo esquema que la API, para señalar el campo antes de
 * enviar. La API vuelve a validar: esto solo ahorra un viaje.
 */
export function validateTenant(d: TenantDraft): { input: TenantInput | null; errors: TenantErrors } {
  const parsed = tenantInputSchema.safeParse({
    slug: d.slug.trim(),
    name: d.name.trim(),
    hubClient: d.hubClient,
    slBaseUrl: d.slBaseUrl.trim(),
    slVerifySsl: d.slVerifySsl,
    slTimeoutMs: Number(d.slTimeoutMs),
    active: d.active,
  });
  if (parsed.success) return { input: parsed.data, errors: {} };
  const errors: TenantErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0] as keyof TenantDraft | undefined;
    if (key && !errors[key]) errors[key] = friendly(key, issue.message);
  }
  return { input: null, errors };
}

function friendly(key: keyof TenantDraft, message: string): string {
  // Los mensajes por defecto de zod salen en inglés; los propios ya están en castellano.
  if (!/^(String|Number|Expected|Invalid|Required)/.test(message)) return message;
  switch (key) {
    case 'name':
      return 'Escribe el nombre del cliente';
    case 'hubClient':
      return 'Elige el túnel del hub';
    case 'slTimeoutMs':
      return 'Entre 1000 y 120000 ms';
    default:
      return 'Valor no válido';
  }
}

const slUrlFor = (ip: string): string => `https://${ip}:50000/b1s/v1`;

export function TenantForm({
  draft,
  onChange,
  errors,
  mode,
  domain,
}: {
  draft: TenantDraft;
  onChange: (d: TenantDraft) => void;
  errors: TenantErrors;
  mode: 'create' | 'edit';
  /** Dominio de los portales, p. ej. `provalia.ecinsapp.es`. */
  domain: string;
}): JSX.Element {
  const tunnels = useAsync(() => api.get<{ tunnels: HubTunnel[] }>('/admin/tunnels').then((r) => r.tunnels), []);
  const set = <K extends keyof TenantDraft>(key: K, value: TenantDraft[K]): void => onChange({ ...draft, [key]: value });

  const list = tunnels.data ?? [];
  // Un túnel guardado que el hub ya no lista se sigue enseñando: si no, el select lo borraría al guardar.
  const options = draft.hubClient && !list.some((t) => t.name === draft.hubClient) ? [{ name: draft.hubClient, ip: null }, ...list] : list;

  const chooseTunnel = (name: string): void => {
    const tunnel = list.find((t) => t.name === name);
    onChange({ ...draft, hubClient: name, slBaseUrl: tunnel?.ip ? slUrlFor(tunnel.ip) : draft.slBaseUrl });
  };

  const slug = draft.slug.trim() || '<identificador>';

  return (
    <div className="stack">
      <div className="grid cols-2">
        <FormField
          label="Identificador"
          error={errors.slug}
          hint={
            mode === 'create' ? (
              <>
                Será <span className="mono">{slug}.{domain}</span> y el nombre de su esquema de datos. No se puede cambiar después.
              </>
            ) : (
              'No se puede cambiar: da nombre al subdominio y al esquema de datos.'
            )
          }
        >
          <input
            className="input mono"
            value={draft.slug}
            readOnly={mode === 'edit'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="imar"
            onChange={(e) => set('slug', e.target.value.toLowerCase())}
          />
        </FormField>
        <FormField label="Nombre" error={errors.name} hint="Razón social o nombre comercial del cliente.">
          <input className="input" value={draft.name} placeholder="IMAR, S.A." onChange={(e) => set('name', e.target.value)} />
        </FormField>
      </div>

      <div className="grid cols-2">
        <FormField
          label="Túnel del hub"
          error={errors.hubClient ?? (tunnels.error ? 'No se pudo leer la lista de túneles del hub' : null)}
          hint={tunnels.loading ? 'Preguntando al hub…' : 'Al elegir un túnel con IP se rellena la URL del Service Layer.'}
        >
          <select className="select" value={draft.hubClient} onChange={(e) => chooseTunnel(e.target.value)} disabled={tunnels.loading}>
            <option value="">Elige un túnel…</option>
            {options.map((t) => (
              <option key={t.name} value={t.name}>
                {t.ip ? `${t.name} · ${t.ip}` : t.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          label="URL del Service Layer"
          error={errors.slBaseUrl}
          hint="Por la IP del cliente dentro de la VPN (10.30.0.X), terminada en /b1s/v1."
        >
          <input
            className="input mono"
            value={draft.slBaseUrl}
            placeholder="https://10.30.0.12:50000/b1s/v1"
            spellCheck={false}
            onChange={(e) => set('slBaseUrl', e.target.value)}
          />
        </FormField>
      </div>

      <div className="grid cols-2">
        <FormField label="Tiempo máximo de espera (ms)" error={errors.slTimeoutMs} hint="Entre 1000 y 120000. Lo normal, 30000.">
          <input className="input num" inputMode="numeric" value={draft.slTimeoutMs} onChange={(e) => set('slTimeoutMs', e.target.value)} />
        </FormField>
        <div className="stack sm form-checks">
          <CheckField
            label="Verificar el certificado SSL"
            hint="Desmarcado si el Service Layer usa el certificado autofirmado de SAP (lo habitual)."
            checked={draft.slVerifySsl}
            onChange={(v) => set('slVerifySsl', v)}
          />
          {mode === 'edit' ? (
            <CheckField
              label="Cliente activo"
              hint="Inactivo: el repaso de SAP se detiene y su portal deja de estar disponible."
              checked={draft.active}
              onChange={(v) => set('active', v)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
